import React, { createContext, useContext, useState, useEffect } from 'react';
import { usersAPI } from '../services/api';

const PermissionsContext = createContext();

export function usePermissions() {
  const context = useContext(PermissionsContext);
  if (!context) {
    throw new Error('usePermissions must be used within a PermissionsProvider');
  }
  return context;
}

// True only for a signed-in Cloud Services administrator; false (instead of an error) where no provider is mounted.
export function useIsCloudAdmin() {
  const context = useContext(PermissionsContext);
  return !!context && context.isAdmin();
}

export function PermissionsProvider({ children, userEmail }) {
  const [role, setRole] = useState(null);
  const [canApprove, setCanApprove] = useState(false);
  const [canReviewBk, setCanReviewBk] = useState(false);
  const [canEditKbFlag, setCanEditKbFlag] = useState(false);
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (userEmail) {
      loadUserRole();
    }
  }, [userEmail]);

  const loadUserRole = async () => {
    try {
      const response = await usersAPI.getByEmail(userEmail);
      if (response.data && response.data.is_active) {
        setRole(response.data.role);
        setCanApprove(!!response.data.can_approve_patches);
        setCanReviewBk(!!response.data.can_review_backups);
        setCanEditKbFlag(!!response.data.can_edit_kb);
        setEmail(response.data.email || '');
      } else {
        console.warn('User found but is inactive');
        setRole('inactive');
      }
    } catch (err) {
      if (err.response && err.response.status === 404) {
        console.warn('User not found in system, access denied');
        // User not in database - no access
        setRole('unauthorized');
      } else {
        console.error('Failed to load user role:', err);
        // On error, deny access
        setRole('unauthorized');
      }
    } finally {
      setLoading(false);
    }
  };

  const isAdmin = () => {
    return role === 'cloud_services';
  };

  const isSupport = () => {
    return role === 'support';
  };

  const canEdit = () => {
    return isAdmin();
  };

  const canCreate = () => {
    return isAdmin();
  };

  const canDelete = () => {
    return isAdmin();
  };

  const canApprovePatches = () => canApprove;
  const canReviewBackups = () => canReviewBk;
  const canEditKb = () => canEditKbFlag || role === 'cloud_services';

  const value = {
    role,
    loading,
    isAdmin,
    isSupport,
    canEdit,
    canCreate,
    canDelete,
    canApprovePatches,
    canReviewBackups,
    canEditKb,
    email,
  };

  return (
    <PermissionsContext.Provider value={value}>
      {children}
    </PermissionsContext.Provider>
  );
}
